import { withApi } from 'contexts/Api/Api';
import { withPage } from 'contexts/Page/Page';
import { withSettings } from 'contexts/Settings/Settings';
import deepmerge from 'deepmerge';
import gsap from 'gsap';
import {
    compose,
    deepClone,
    fillSteps,
    getHurricaneBands,
    getNormalDistribution,
    getRandomTruckName,
    getResults,
    getTime,
    getWeeklyResults,
    parseStepInitialBounds,
    parseStepRanges,
    sliceResultsByWeek,
    transformValue,
} from 'helpers';
import _ from 'lodash';
import { withRouter } from 'next/router';
import { Component, createContext, useContext } from 'react';
import * as setup from 'setup';

const initialSteps = _.range(setup.game.steps.length).map(index => ({
    bounds: {
        max: 0,
        min: 0,
    },
    conversion: {
        max: 0,
        min: 0,
    },
    index,
    initial: 0,
    max: 0,
    min: 0,
    placeholder: 0,
    retention: {
        max: setup.game.retention.value,
        min: setup.game.retention.value,
    },
}));

export const GameContext = createContext<GameState>({} as GameState);

export const useGame = () => useContext(GameContext);

class Game extends Component<GameProviderProps, GameState> {
    progressSyncInFlight = new Set<string>();

    initialState = {
        foodType: '',
        hurricaneBands: {
            green: [],
            orange: [],
            red: [],
            yellow: [],
        },
        predictions: {
            conversions: 0,
            initial: setup.game.prediction.default / 28,
            initialMonthly: setup.game.prediction.default,
            steps: 0,
        },
        results: {
            bases: _.range(setup.game.steps.length).map(() => ({
                max: 0,
                min: 0,
                conversion: 0,
                step: 0,
            })),
            days: [],
        },
        steps: parseStepInitialBounds(initialSteps, setup.game.prediction.default / 28),
        truckName: getRandomTruckName(),
    };

    constructor(props: GameProviderProps) {
        super(props);

        // Get history
        const time = getTime();
        const prevTime = parseInt(window.localStorage.getItem('iaa.game.history.time') || `${time}`);
        const expired = !!setup.game.history.expiration && time - prevTime > setup.game.history.expiration;
        const history = JSON.parse((!expired && window.localStorage.getItem('iaa.game.history')) || '{}');

        // Initialize state
        this.state = {
            ...this.initialState,
            history,
            initialized: !props.page.lesson,
            lesson: null,
            lessonProps: {},
            lessonReady: false,
            ready: false,
            animateStepRange: this.animateStepRange,
            fillSteps: this.fillSteps,
            getHistory: this.getHistory,
            regenerate: this.regenerate,
            resetLessonState: this.resetLessonState,
            setFoodType: this.setFoodType,
            setLessonProps: this.setLessonProps,
            setTruckName: this.setTruckName,
            storeHistory: this.storeHistory,
            updatePredictions: this.updatePredictions,
            updateStep: this.updateStep,
        };
    }

    isPublicLessonShell = () => {
        const { api, page } = this.props;
        return !!page.lesson && api.authenticate && !api.loadingAuthState && !api.authenticated;
    };

    getServerCheckpointIndex = () => {
        const progress = this.props.api.gameProgress['food-truck'];
        const lastCheckpointId = progress?.last_checkpoint_id;
        const highestCheckpointId = progress?.highest_checkpoint_id;
        const lastCheckpointRank = progress?.last_checkpoint_rank || 0;
        const highestCheckpointRank = progress?.highest_checkpoint_rank || 0;

        const checkpointId = lastCheckpointId || highestCheckpointId;
        const checkpointIndex = checkpointId ? setup.lessonKeys.indexOf(checkpointId) : -1;
        const rankIndex = Math.max(lastCheckpointRank, highestCheckpointRank) - 1;

        return Math.max(checkpointIndex, rankIndex, 0);
    };

    componentDidUpdate() {
        const { lesson } = this.state;

        if (this.isPublicLessonShell()) {
            return;
        }

        // Update lesson
        if (this.props.page.lesson && this.props.page.lesson !== lesson) {
            this.updateLesson(this.props.page.lesson, lesson, this.props.page.interacted);
        }
    }

    updateLesson = (lesson: string, prevLesson: string | null, interacted: boolean) => {
        const { settings } = this.props;
        const { history, ready } = this.state;

        // Check current lesson based on history
        if (!ready) {
            const lessonIndex = setup.lessonKeys.indexOf(lesson);
            const localLastLesson = Object.keys(history).pop()?.split('-')[0];
            const localLastIndex = localLastLesson ? setup.lessonKeys.indexOf(localLastLesson) : 0;
            const serverLastIndex = this.getServerCheckpointIndex();
            const lastIndex = Math.max(localLastIndex, serverLastIndex, 0);
            const fallbackLastLesson = setup.lessonKeys[lastIndex] || setup.lessonKeys[0];
            if (!settings.debugProgress && (lessonIndex === -1 || lessonIndex > lastIndex)) {
                const redirect = fallbackLastLesson ? `lessons/${fallbackLastLesson}` : '';
                window.location.href = `${process.env.NEXT_CONFIG_BASE_PATH || ''}/${redirect}`;
                return;
            }
        }

        // Story previous lesson in history
        if (prevLesson) {
            this.storeHistory(prevLesson, 'end');
        }

        // Prepare state for update
        this.setState(
            {
                initialized: true,
                lesson,
                lessonReady: false,
                ready: false,
            },
            async () => {
                // Get lesson props based on action
                const lessonAction = interacted ? 'load' : 'restore';
                const lessonProps = await this.getLessonActionProps(lesson, lessonAction);

                // Check questions lesson requirement
                if (lessonProps.requireLesson?.length) {
                    return this.props.page.show(lessonProps.requireLesson[0]);
                }

                // Set lesson props
                this.setLessonProps(
                    lessonProps,
                    () => {
                        // Store lesson on load
                        if (lessonAction === 'load') {
                            this.storeHistory(lesson, 'start');
                        }
                    },
                    interacted
                );
            }
        );
    };

    resetLessonState = async () => {
        if (this.state.lesson) {
            const lessonProps = await this.getLessonActionProps(this.state.lesson, 'restore');
            this.setLessonProps(lessonProps);
        }
    };

    getLessonActionProps = async (lesson: string, lessonAction: GameLessonAction) => {
        // Get page props
        const pageProps = await import(`../../pages/lessons/${lesson}`).then(mod => mod.props).catch(() => ({}));

        // Get lesson props from page and history based on action
        const lessonPageProps = (typeof pageProps === 'function' ? pageProps(this.state) : { ...pageProps }) || {};
        const lessonHistory =
            (lessonAction === 'restore' && (this.getHistory(lesson, 'end') || this.getHistory(lesson, 'start'))) ||
            null;
        const lessonProps: GameLessonProps = {
            ...lessonPageProps,
            ...lessonHistory,
        };

        // Check history restorations
        lessonProps.history?.restores?.find(restore => {
            const [l, t] = restore.split('-') as any;
            const restoreHistory = this.getHistory(l, t);
            if (restoreHistory) {
                lessonProps.reset = true;
            }
            return restoreHistory;
        });

        return lessonProps;
    };

    setLessonProps = (lessonProps: CallableGameLessonProps, callback?: Callback, interacted?: boolean) => {
        // Update state
        this.setState(
            prevState => {
                const props = typeof lessonProps === 'function' ? lessonProps(prevState) : lessonProps;

                // Prepare new state
                const newState: GameState = {
                    ...prevState,
                    ...(props.reset ? this.initialState : {}),
                    lessonProps: props,
                    lessonReady: true,
                };

                // Check history removals
                if (interacted && props.history?.removes) {
                    Object.keys(newState.history).forEach(key => {
                        if (
                            props.history?.removes?.find(
                                removeKey => key === removeKey || key.startsWith(`${removeKey}-`)
                            )
                        ) {
                            delete newState.history[key];
                        }
                    });
                }

                // Apply value props
                Object.entries(props).forEach(([key, value]) => {
                    if (value !== undefined && !['fillSteps'].includes(key)) {
                        (newState as any)[key] = value;
                    }
                });

                // Fill steps
                if (props.fillSteps) {
                    newState.steps = fillSteps(
                        newState.steps,
                        newState.results,
                        newState.predictions.initial,
                        props.fillSteps
                    );
                }

                // Update predictions
                if (props.updatePrediction) {
                    newState.predictions[props.updatePrediction] = newState.steps[4].max;
                }

                // Calculate results
                if (props.calculateResults) {
                    newState.results = getResults(
                        newState.results,
                        newState.predictions.initial,
                        props.calculateResults
                    );
                }

                // Calculate hurricane bands
                if (props.calculateHurricaneBands) {
                    newState.hurricaneBands = getHurricaneBands(newState.steps);
                }

                return newState;
            },
            async () => {
                // Complete
                this.setState(
                    {
                        lessonReady: true,
                        ready: true,
                    },
                    callback
                );
            }
        );
    };

    getHistory = (lesson: string, type?: GameHistoryType) => {
        // Get by type
        if (type) {
            const key = `${lesson}-${type}`;
            return this.state.history[key] || null;
        }

        // Default to end or start
        const endKey = `${lesson}-end`;
        const startKey = `${lesson}-start`;
        return this.state.history[endKey] || this.state.history[startKey] || null;
    };

    syncLessonProgress = async (lesson: string, type: GameHistoryType) => {
        const { api } = this.props;
        if (!api.authenticate || !api.authenticated) {
            return;
        }

        const rank = setup.lessonKeys.indexOf(lesson) + 1;
        if (!rank) {
            return;
        }

        const syncKey = `${lesson}-${type}`;
        if (this.progressSyncInFlight.has(syncKey)) {
            return;
        }

        this.progressSyncInFlight.add(syncKey);

        try {
            await api.upsertGameProgress('food-truck', {
                completed: rank === setup.lessonKeys.length,
                highest_checkpoint_id: lesson,
                highest_checkpoint_rank: rank,
                last_checkpoint_id: lesson,
                last_checkpoint_rank: rank,
                state: {
                    history_type: type,
                    lesson,
                },
            });
        } catch {
            // Keep gameplay uninterrupted when progression sync fails.
        } finally {
            this.progressSyncInFlight.delete(syncKey);
        }
    };

    storeHistory = (lesson: string, type: GameHistoryType) => {
        // Add current state to history
        this.setState(
            prevState => {
                // Check store skip
                if (prevState.lessonProps.history?.store === false) {
                    return null;
                }

                // Add lesson to history
                return {
                    history: {
                        ...prevState.history,
                        [`${lesson}-${type}`]: {
                            foodType: prevState.foodType,
                            predictions: prevState.predictions,
                            reset: lesson === prevState.lesson && !!prevState.lessonProps.reset,
                            results: prevState.results,
                            steps: prevState.steps,
                            truckName: prevState.truckName,
                        },
                    },
                };
            },
            () => {
                // Store history in local storage
                const history = JSON.stringify(this.state.history);
                window.localStorage.setItem('iaa.game.history', history);
                window.localStorage.setItem('iaa.game.history.time', getTime().toString());

                void this.syncLessonProgress(lesson, type);
            }
        );
    };

    setFoodType = (foodType: string) => {
        this.setState({
            foodType,
        });
    };

    updatePredictions = (data: Partial<GamePredictions>) => {
        const initial = data.initialMonthly ? data.initialMonthly / 28 : 0;
        this.setState(prevState => ({
            predictions: {
                ...prevState.predictions,
                ...data,
                initial: initial || prevState.predictions.initial,
            },
            steps: initial ? parseStepInitialBounds(prevState.steps, initial) : prevState.steps,
        }));
    };

    setTruckName = (truckName: string) => {
        this.setState({
            truckName,
        });
    };

    updateStep = (index: number, data: GameStepData, init?: boolean) => {
        return new Promise<GameStep>(resolve => {
            this.setState(
                prevState => {
                    const { predictions, steps } = prevState;

                    // Update step
                    let newSteps = deepClone(steps);
                    const step = deepmerge(newSteps[index], data) as GameStep;
                    newSteps[index] = step;

                    // Update previous step conversion
                    if (index && (data.min !== undefined || data.max !== undefined)) {
                        const prevStep = newSteps[index - 1];
                        prevStep.conversion.min = data.min !== undefined && prevStep.min ? data.min / prevStep.min : 0;
                        prevStep.conversion.max = data.max !== undefined && prevStep.max ? data.max / prevStep.max : 0;
                    }

                    // Update bounds
                    if (init) {
                        newSteps = parseStepInitialBounds(newSteps, predictions.initial);
                    }

                    // Update state
                    return {
                        steps: init ? newSteps : parseStepRanges(newSteps),
                    };
                },
                () => {
                    // Resolve updated step
                    const step = this.state.steps[index];
                    resolve(step);
                }
            );
        });
    };

    fillSteps = (type: GameFillStepsType) => {
        this.setState(prevState => ({
            steps: fillSteps(prevState.steps, prevState.results, prevState.predictions.initial, type),
        }));
    };

    animateStepRange = (index: number, type: GameAnimateStepRangeType) => {
        const setupStep = setup.game.steps[index];
        const { steps } = this.state;
        const step = steps[index];
        const conversion = type === 'conversion' ? step.conversion : null;
        const retention = type === 'retention' ? step.retention : null;

        // Get source
        const source = conversion || retention || step;
        if (source.min !== source.max) {
            return;
        }

        // Prepare
        const decimals =
            (conversion && setupStep.conversion.decimals) || (retention && setup.game.retention.decimals) || 0;
        const multiplier =
            (conversion && setupStep.conversion.multiplier) || (retention && setup.game.retention.multiplier) || 1;
        const values = {
            max: source.max,
            min: source.max,
        };

        // Animate
        gsap.to(values, {
            delay: 0.2,
            min: transformValue(source.max * 0.9, multiplier, decimals, true),
            max: transformValue(source.max * 1.1, multiplier, decimals, true),
            onUpdate: () => {
                let data: any = {
                    min: transformValue(values.min, multiplier, decimals, true),
                    max: transformValue(values.max, multiplier, decimals, true),
                };
                if (type !== 'step') {
                    data = {
                        [type]: data,
                    };
                }
                this.updateStep(index, data);
            },
        });
    };

    regenerate = (all?: boolean) => {
        // TODO: Is there a better, non-duplicate way to do this?
        this.setState(prevState => {
            const newState = {
                ...prevState,
                predictions: { ...prevState.predictions },
                results: { ...prevState.results },
                steps: deepClone(prevState.steps),
            };

            // Check lesson
            const setupLessonsMap = Object.fromEntries(setup.lessonKeys.map((lesson, index) => [lesson, index]));
            const lessonIndex = (prevState.lesson && setupLessonsMap[prevState.lesson]) || 0;
            const checkLesson = (lesson: string) =>
                all ? lessonIndex >= setupLessonsMap[lesson] : prevState.lesson === lesson;

            // Lesson 1
            if (checkLesson('1.6')) {
                newState.results = getResults(newState.results, newState.predictions.initial, 4);
            }

            // Lesson 2
            if (checkLesson('2.9')) {
                newState.steps = fillSteps(newState.steps, newState.results, newState.predictions.initial, 'initials');
            }
            if (checkLesson('2.14')) {
                newState.predictions.steps = newState.steps[4].max;
            }
            if (checkLesson('2.15')) {
                newState.results = getResults(newState.results, newState.predictions.initial, 5);
            }

            // Lesson 3
            if (checkLesson('3.2')) {
                const resultsWeek5 = sliceResultsByWeek(newState.results.days, 5);
                newState.steps[0].max = _.round(_.mean(resultsWeek5.map(day => day.step1)));
            }
            if (checkLesson('3.4')) {
                newState.steps = fillSteps(
                    newState.steps,
                    newState.results,
                    newState.predictions.initial,
                    'conversions'
                );
            }
            if (checkLesson('3.13')) {
                newState.predictions.conversions = newState.steps[4].max;
                newState.results = getResults(newState.results, newState.predictions.initial, 6);
            }

            // Lesson 4
            if (checkLesson('4.10')) {
                const resultsWeek6 = sliceResultsByWeek(newState.results.days, 5, 6);
                const step1 = newState.steps[0];
                const step1Distribution = getNormalDistribution(resultsWeek6.map(day => day.step1));
                step1.min = Math.round(step1Distribution.lower);
                step1.max = Math.round(step1Distribution.upper);

                newState.steps = fillSteps(newState.steps, newState.results, newState.predictions.initial, 'ranges');
            }
            if (checkLesson('4.20')) {
                newState.results = getResults(newState.results, newState.predictions.initial, 8);
            }

            // Lesson 5
            if (checkLesson('5.1')) {
                newState.results = getResults(newState.results, newState.predictions.initial, 28);
            }
            if (checkLesson('5.8')) {
                const weeklyResults = getWeeklyResults(newState.results.days).slice(4);
                const retentionDistribution = getNormalDistribution(weeklyResults.map(week => week.step4Retention));
                const step4 = newState.steps[3];
                step4.retention.min = retentionDistribution.lower;
                step4.retention.max = retentionDistribution.upper;
            }
            if (checkLesson('5.9')) {
                newState.hurricaneBands = getHurricaneBands(newState.steps);
            }

            return newState;
        });
    };

    render() {
        const { children, page, router } = this.props;
        const { initialized, lesson, lessonReady, ready } = this.state;
        const publicLessonShell = this.isPublicLessonShell();

        // Parse state
        const state = {
            ...this.state,
            ready: !publicLessonShell && ready && lessonReady && !!page.lesson && router.asPath === `/lessons/${lesson}`,
        };

        return <GameContext.Provider value={state}>{(initialized || publicLessonShell || !page.lesson) && children}</GameContext.Provider>;
    }
}

export const GameProvider = compose(withApi, withPage, withRouter, withSettings)(Game);
